/**
 * Quota builder for slot booking, used from the Supports event settings form.
 *
 * The buyer-facing picker only understands two dimensions, in this order: the
 * first is the date the calendar shows, the second is the slot. It also reads the
 * slot labels from `value_map.slot` by that exact name. So a slot key always looks
 * like `option--date--[DATE]:option--slot--[SLOT]`, and this builder edits exactly
 * that shape.
 *
 * Anything that does not fit is left to the JSON editor untouched, so an event
 * written by hand is never silently rewritten.
 */

export interface TicketTypeOption {
  key: string
  name: string
}

export type QuotaCell = number | Record<string, number>
export type Quotas = Record<string, QuotaCell>

/** Which cells a bulk fill writes: only the empty ones, or every one. */
export type FillScope = "empty" | "all"

export interface DimensionOption {
  value: string
  label: string
}

/** Where the builder reads and writes the JSON the form actually submits. */
export interface QuotaBuilderPorts {
  readJson(): string
  writeJson(json: string): void
}

export interface SlotBookingQuotaBuilderOptions {
  ticketTypes?: TicketTypeOption[]
  quotaKey?: string
  textareaId?: string
  ports?: QuotaBuilderPorts
}

const SLOT_KEY_SHAPE = /^option--date--[^:]+:option--slot--[^:]+$/
const DAY_IN_MS = 24 * 60 * 60 * 1000

/**
 * A slot is one hour unless an operator types something else. Every slot booking
 * event so far runs on the hour, so the builder offers the next hour and lets the
 * operator type over it.
 */
const SLOT_LENGTH_MINUTES = 60
const MINUTES_IN_DAY = 24 * 60

/** A slot value is a clock time with a hyphen, because a colon splits a slot key. */
function parseSlotMinutes(value: string): number | null {
  const match = /^(\d{1,2})-(\d{2})$/.exec((value || "").trim())
  if (!match) return null

  const hours = Number(match[1])
  const minutes = Number(match[2])
  if (hours > 23 || minutes > 59) return null

  return hours * 60 + minutes
}

function formatClock(minutes: number): string {
  const wrapped = ((minutes % MINUTES_IN_DAY) + MINUTES_IN_DAY) % MINUTES_IN_DAY
  const hours = String(Math.floor(wrapped / 60)).padStart(2, "0")
  return `${hours}:${String(wrapped % 60).padStart(2, "0")}`
}

function formatSlotValue(minutes: number): string {
  return formatClock(minutes).replace(":", "-")
}

/** UTC on purpose: a local-time Date shifts the day across a timezone boundary. */
function parseIsoDate(value: string): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value || "")) return null
  const parsed = new Date(`${value}T00:00:00Z`)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

function* eachIsoDate(first: Date, last: Date): Generator<string> {
  for (let day = first.getTime(); day <= last.getTime(); day += DAY_IN_MS) {
    yield new Date(day).toISOString().slice(0, 10)
  }
}

/**
 * Reads and writes the textarea the form submits. CodeMirror is kept in step
 * because the form copies the editor back over the textarea on submit, so a stale
 * editor would undo everything typed in the builder.
 */
function domPorts(textareaId: string): QuotaBuilderPorts {
  const textarea = () =>
    typeof document === "undefined"
      ? null
      : (document.getElementById(textareaId) as HTMLTextAreaElement | null)

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const editor = () => (typeof window === "undefined" ? null : (window as any).slotBookingEditor)

  return {
    readJson() {
      const codeMirror = editor()
      if (codeMirror) return codeMirror.getValue()
      return textarea()?.value ?? "{}"
    },
    writeJson(json: string) {
      const field = textarea()
      if (field) field.value = json
      const codeMirror = editor()
      if (codeMirror) codeMirror.setValue(json)
    },
  }
}

export function slotBookingQuotaBuilder(options: SlotBookingQuotaBuilderOptions = {}) {
  const ports = options.ports ?? domPorts(options.textareaId ?? "event_slot_booking_settings")

  return {
    dates: [] as DimensionOption[],
    slots: [] as DimensionOption[],
    quotas: {} as Quotas,
    ticketTypes: options.ticketTypes ?? [],
    quotaKey: options.quotaKey ?? "none",
    mode: "builder" as "builder" | "json",
    unsupported: false,
    periodStart: "",
    periodUntil: "",
    fillValue: "",
    fillScope: "empty" as FillScope,
    /** The ticket type a bulk fill writes. An empty string means every ticket type. */
    fillTicketType: "",

    init() {
      this.load()

      if (typeof document === "undefined") return
      const select = document.getElementById("event_slot_booking_quota_key") as HTMLSelectElement | null
      if (!select) return

      select.addEventListener("change", () => {
        const previous = this.quotaKey
        this.quotaKey = select.value
        // `load` leaves the builder empty when it cannot read the JSON. Writing from
        // that state would replace the operator's own JSON with empty structures.
        if (this.unsupported) return
        this.convertQuotas(previous, select.value)
        this.sync()
      })
    },

    load() {
      let parsed: { quotas?: Quotas; value_map?: Record<string, Record<string, string>> }

      try {
        parsed = JSON.parse(ports.readJson() || "{}")
      } catch {
        this.fallBackToJson()
        return
      }

      const valueMap = parsed.value_map ?? {}
      const quotas = parsed.quotas ?? {}

      const extraDimensions = Object.keys(valueMap).filter((key) => key !== "date" && key !== "slot")
      const oddKeys = Object.keys(quotas).filter((key) => !SLOT_KEY_SHAPE.test(key))
      if (extraDimensions.length > 0 || oddKeys.length > 0) {
        this.fallBackToJson()
        return
      }

      this.unsupported = false
      this.dates = Object.entries(valueMap.date ?? {}).map(([value, label]) => ({ value, label }))
      this.slots = Object.entries(valueMap.slot ?? {}).map(([value, label]) => ({ value, label }))
      this.quotas = quotas

      // A date or slot used by a quota but missing from value_map still has to
      // appear, or saving from the builder would silently drop its quota.
      Object.keys(quotas).forEach((key) => {
        const [datePart, slotPart] = key.split(":")
        const dateValue = datePart.split("--")[2]
        const slotValue = slotPart.split("--")[2]
        if (!this.dates.some((date) => date.value === dateValue)) {
          this.dates.push({ value: dateValue, label: dateValue })
        }
        if (!this.slots.some((slot) => slot.value === slotValue)) {
          this.slots.push({ value: slotValue, label: slotValue })
        }
      })

      this.dates.sort((a, b) => a.value.localeCompare(b.value))
    },

    fallBackToJson() {
      this.unsupported = true
      this.mode = "json"
    },

    setMode(mode: "builder" | "json") {
      if (mode === "builder") {
        if (this.unsupported) return
        this.load()
      }
      this.mode = mode

      // CodeMirror measures itself on init. It was hidden then, so it needs a
      // refresh once the JSON view is actually on screen.
      if (mode === "json" && typeof window !== "undefined") {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const editor = (window as any).slotBookingEditor
        if (editor) setTimeout(() => editor.refresh(), 0)
      }
    },

    validDates(): DimensionOption[] {
      return this.dates.filter((date) => date.value)
    },

    validSlots(): DimensionOption[] {
      return this.slots.filter((slot) => slot.value)
    },

    slotKeyFor(dateValue: string, slotValue: string): string {
      return `option--date--${dateValue}:option--slot--${slotValue}`
    },

    quotaAt(dateValue: string, slotValue: string, ticketTypeKey: string | null): number | "" {
      const cell = this.quotas[this.slotKeyFor(dateValue, slotValue)]
      if (cell === undefined || cell === null) return ""
      if (ticketTypeKey) {
        return typeof cell === "object" ? (cell[ticketTypeKey] ?? "") : ""
      }
      return typeof cell === "object" ? "" : cell
    },

    /**
     * Quota keys present in the data that are not a ticket type of this event, for
     * example a ticket type that was deleted. The grid cannot draw them, so they are
     * named instead of quietly sitting in the JSON.
     */
    strayQuotaKeys(): string[] {
      if (this.quotaKey !== "ticket_type") return []

      const known = new Set(this.ticketTypes.map((ticketType) => ticketType.key))
      const stray = new Set<string>()
      Object.values(this.quotas).forEach((cell) => {
        if (typeof cell !== "object" || cell === null) return
        Object.keys(cell).forEach((key) => {
          if (!known.has(key)) stray.add(key)
        })
      })
      return [...stray].sort()
    },

    /**
     * `none` stores one number per slot; `ticket_type` stores one number per ticket
     * type. Changing the quota key leaves every cell in the wrong shape, and a cell
     * in the wrong shape never matches at order time — the slot just cannot be
     * booked. So the numbers are carried into the new shape here, where the operator
     * can still see and correct them before saving.
     */
    convertQuotas(from: string, to: string) {
      if (from === to) return

      const converted: Quotas = {}

      Object.entries(this.quotas).forEach(([key, cell]) => {
        if (to === "ticket_type") {
          if (typeof cell === "object" && cell !== null) {
            converted[key] = cell
            return
          }
          // One pool of `cell` places becomes that many places for each ticket type.
          // Every ticket type keeps its own pool under this quota key.
          const perTicketType: Record<string, number> = {}
          this.ticketTypes.forEach((ticketType) => {
            perTicketType[ticketType.key] = cell
          })
          if (Object.keys(perTicketType).length > 0) converted[key] = perTicketType
          return
        }

        if (typeof cell !== "object" || cell === null) {
          converted[key] = cell
          return
        }
        // The separate pools collapse back into one pool of the same total size.
        converted[key] = Object.values(cell).reduce((sum, value) => sum + value, 0)
      })

      this.quotas = converted
    },

    setQuota(dateValue: string, slotValue: string, ticketTypeKey: string | null, raw: string) {
      const parsed = raw === "" ? null : parseInt(raw, 10)
      const value = parsed === null || Number.isNaN(parsed) ? null : parsed

      this.writeQuota(this.slotKeyFor(dateValue, slotValue), ticketTypeKey, value)
      this.sync()
    },

    /**
     * Changes one number without writing the JSON. A bulk fill can change hundreds
     * of cells, and each write also resets the code editor, so it writes once at
     * the end instead.
     */
    writeQuota(key: string, ticketTypeKey: string | null, value: number | null) {
      if (ticketTypeKey) {
        const existing = this.quotas[key]
        const cell: Record<string, number> =
          typeof existing === "object" && existing !== null ? { ...existing } : {}

        if (value === null) {
          delete cell[ticketTypeKey]
        } else {
          cell[ticketTypeKey] = value
        }

        if (Object.keys(cell).length === 0) {
          delete this.quotas[key]
        } else {
          this.quotas[key] = cell
        }
      } else if (value === null) {
        delete this.quotas[key]
      } else {
        this.quotas[key] = value
      }
    },

    addDate() {
      this.dates.push({ value: "", label: "" })
    },

    removeDate(index: number) {
      const [removed] = this.dates.splice(index, 1)
      if (removed?.value) this.dropQuotasFor("date", removed.value)
      this.sync()
    },

    addSlot() {
      const value = this.nextSlotValue()
      this.slots.push({ value, label: value ? this.defaultSlotLabel(value) : "" })
      if (value) this.sync()
    },

    /**
     * The hour after the last slot that reads as a time. The operator adds a row and
     * tabs past it, rather than typing the same hour again on every row.
     *
     * An empty string leaves the row blank, which is right when there is nothing to
     * continue from and when the next hour is already in the list.
     */
    nextSlotValue(): string {
      for (let index = this.slots.length - 1; index >= 0; index -= 1) {
        const start = parseSlotMinutes(this.slots[index].value)
        if (start === null) continue

        const candidate = formatSlotValue(start + SLOT_LENGTH_MINUTES)
        return this.slots.some((slot) => slot.value === candidate) ? "" : candidate
      }

      return ""
    },

    removeSlot(index: number) {
      const [removed] = this.slots.splice(index, 1)
      if (removed?.value) this.dropQuotasFor("slot", removed.value)
      this.sync()
    },

    dropQuotasFor(dimension: "date" | "slot", value: string) {
      const marker = `option--${dimension}--${value}`
      Object.keys(this.quotas).forEach((key) => {
        if (key.split(":").includes(marker)) delete this.quotas[key]
      })
    },

    /**
     * Renaming carries the quotas across. Without this, fixing a typo in a date
     * would quietly zero every number under it.
     */
    renameOption(dimension: "date" | "slot", index: number, newValue: string) {
      const list = dimension === "date" ? this.dates : this.slots
      const oldValue = list[index].value
      list[index].value = newValue

      // A row with no display text reads as a blank column in the picker, so the
      // display follows the label until an operator types over it.
      if (!list[index].label || list[index].label === this.defaultLabelFor(dimension, oldValue)) {
        list[index].label = this.defaultLabelFor(dimension, newValue)
      }

      if (!oldValue || oldValue === newValue) {
        this.sync()
        return
      }

      const from = `option--${dimension}--${oldValue}`
      const to = `option--${dimension}--${newValue}`
      const moved: Quotas = {}
      Object.keys(this.quotas).forEach((key) => {
        const parts = key.split(":").map((part) => (part === from ? to : part))
        moved[parts.join(":")] = this.quotas[key]
      })
      this.quotas = moved
      this.sync()
    },

    defaultDateLabel(value: string): string {
      const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || "")
      return match ? `${match[3]}/${match[2]}/${match[1]}` : value
    },

    /**
     * `11-00` reads as `11:00-12:00`. The value is what the picker matches on, and
     * the label is what a buyer sees, so a bare `11-00` on the page says nothing
     * about when the slot ends.
     */
    defaultSlotLabel(value: string): string {
      const start = parseSlotMinutes(value)
      if (start === null) return value

      return `${formatClock(start)}-${formatClock(start + SLOT_LENGTH_MINUTES)}`
    },

    defaultLabelFor(dimension: "date" | "slot", value: string): string {
      return dimension === "date" ? this.defaultDateLabel(value) : this.defaultSlotLabel(value)
    },

    /**
     * Adds one date for every day from `periodStart` to `periodUntil`. A date that
     * is already in the list keeps its display text and its quotas, so running the
     * generator twice over an overlapping range changes nothing.
     */
    generatePeriod(): number {
      const first = parseIsoDate(this.periodStart)
      const last = parseIsoDate(this.periodUntil)
      if (!first || !last || first > last) return 0

      let added = 0
      for (const value of eachIsoDate(first, last)) {
        if (this.dates.some((date) => date.value === value)) continue
        this.dates.push({ value, label: this.defaultDateLabel(value) })
        added += 1
      }

      this.dates.sort((a, b) => a.value.localeCompare(b.value))
      this.sync()
      return added
    },

    periodDayCount(): number {
      const first = parseIsoDate(this.periodStart)
      const last = parseIsoDate(this.periodUntil)
      if (!first || !last || first > last) return 0
      return Math.round((last.getTime() - first.getTime()) / DAY_IN_MS) + 1
    },

    /** Sums a cell whichever shape it is in, so one total covers both quota keys. */
    cellTotal(dateValue: string, slotValue: string): number {
      const cell = this.quotas[this.slotKeyFor(dateValue, slotValue)]
      if (cell === undefined || cell === null) return 0
      if (typeof cell === "object") {
        return Object.values(cell).reduce((sum, value) => sum + value, 0)
      }
      return cell
    },

    dateTotal(dateValue: string): number {
      return this.validSlots().reduce(
        (sum, slot) => sum + this.cellTotal(dateValue, slot.value),
        0,
      )
    },

    slotTotal(slotValue: string): number {
      return this.validDates().reduce(
        (sum, date) => sum + this.cellTotal(date.value, slotValue),
        0,
      )
    },

    grandTotal(): number {
      return this.validDates().reduce((sum, date) => sum + this.dateTotal(date.value), 0)
    },

    /**
     * Writes `fillValue` into the grid and returns how many numbers changed.
     *
     * With `fillScope` "empty", a filled cell is left alone, so the fill never
     * overwrites a number an operator typed. With "all", every cell gets the value.
     * Under the `ticket_type` quota key, `fillTicketType` limits the fill to one
     * ticket type.
     */
    fillQuotas(): number {
      const parsed = parseInt(this.fillValue, 10)
      if (Number.isNaN(parsed) || parsed < 0) return 0

      const ticketTypeKeys = this.fillTicketTypeKeys()

      let filled = 0
      this.validDates().forEach((date) => {
        this.validSlots().forEach((slot) => {
          ticketTypeKeys.forEach((ticketTypeKey) => {
            const current = this.quotaAt(date.value, slot.value, ticketTypeKey)
            if (current === parsed) return
            if (this.fillScope === "empty" && current !== "") return

            this.writeQuota(this.slotKeyFor(date.value, slot.value), ticketTypeKey, parsed)
            filled += 1
          })
        })
      })

      if (filled > 0) this.sync()
      return filled
    },

    /**
     * The quota keys a fill writes in each cell. `null` is the single number of the
     * `none` quota key. The ticket type choice is ignored under `none`, because the
     * grid does not show it there.
     */
    fillTicketTypeKeys(): (string | null)[] {
      if (this.quotaKey !== "ticket_type") return [null]

      return this.ticketTypes
        .filter((ticketType) => !this.fillTicketType || ticketType.key === this.fillTicketType)
        .map((ticketType) => ticketType.key)
    },

    build() {
      const valueMap: { date: Record<string, string>; slot: Record<string, string> } = {
        date: {},
        slot: {},
      }

      this.dates.forEach((date) => {
        if (date.value) valueMap.date[date.value] = date.label || this.defaultDateLabel(date.value)
      })
      this.slots.forEach((slot) => {
        if (slot.value) valueMap.slot[slot.value] = slot.label || slot.value
      })

      return { quotas: this.quotas, value_map: valueMap }
    },

    sync() {
      // `load` leaves the builder empty when it cannot read the JSON, and the JSON
      // editor owns the value from then on. Writing would replace what the operator
      // is editing by hand with empty structures.
      if (this.unsupported) return

      ports.writeJson(JSON.stringify(this.build(), null, 2))
    },
  }
}

export default slotBookingQuotaBuilder
